import React from 'react';
import { isBlank } from '../utils/common-functions';

interface TextInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  onEnterKeyPressed?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  width?: string;
  fieldType?: string;
  errorMessage?: string;
  required?: boolean;
  autocomplete?: string;
  validation?: (value: string) => boolean;
  custom_test_id?: string;
  showValidationError?: boolean;
}

const defaultValidation = (required: boolean) => (value: string) => {
  return !required || (required && !isBlank(value));
};

function validateConsideringBlank(
  value: string,
  required: boolean,
  dirty: boolean,
  validationFn: (value: string) => boolean,
  showValidationError: boolean,
): boolean {
  if (isBlank(value)) {
    if (!required) {
      // 1) If the field is blank but not required, it's valid
      return true;
    }

    if (!dirty) {
      // 2) If field is blank and not dirty, don't show validation error (return true)
      return true;
    } else {
      // 3) If field is blank, required and dirty,
      // show validation error only if showValidationError is true (default is true)
      // showing error message ==> returning false in validation function
      return !showValidationError;
    }
  }
  return validationFn(value);
}

export const TextInput: React.FC<TextInputProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Type here',
  onEnterKeyPressed,
  disabled = false,
  width = 'w-1/4',
  fieldType = 'text',
  errorMessage = 'This field is incorrect or required',
  required = false,
  autocomplete = 'off',
  validation = defaultValidation(required),
  custom_test_id,
  showValidationError = true,
  ...rest
}) => {
  // state for keeping track of whether the input has been modified
  const [dirty, setDirty] = React.useState(false);

  const isValid = validateConsideringBlank(
    value,
    required,
    dirty,
    validation,
    showValidationError,
  );

  const showErrorMessage = !isValid;

  const inputClass = showErrorMessage
    ? 'input input-bordered input-error w-full'
    : 'input input-bordered input-primary w-full';

  return (
    <label key={'text-input-div_' + label} className={'form-control ' + width}>
      <div className="label">
        <span className="label-text">{label}</span>
      </div>
      <input
        key={'text-input_' + label}
        data-testid={custom_test_id}
        type={fieldType}
        placeholder={placeholder}
        autoComplete={autocomplete}
        className={inputClass}
        onChange={(e) => {
          setDirty(true);
          onChange(e);
        }}
        onKeyDown={(e) =>
          e.key === 'Enter' && onEnterKeyPressed && onEnterKeyPressed(e)
        }
        disabled={disabled}
        {...(fieldType !== 'file' ? { value } : {})}
        {...rest}
      />
      <p className="mt-2 text-sm text-error">
        <span className="font-medium">
          {showErrorMessage ? errorMessage : ''}
        </span>
      </p>
    </label>
  );
};
